import { useMemo, useRef, type ReactNode } from 'react';
import {
  RememberedEventListWindowsContext,
  type EventListWindow,
  type RememberedWindows,
} from './useRememberedEventListWindow';

// The ref lives here rather than in the exported provider so the reset below is a
// remount instead of a write during render. React does not roll ref writes back
// when it abandons a render, so clearing the map as the id changed could have
// discarded one planner's windows on a speculative render for another that was
// never committed.
const RememberedWindowsScope = ({ children }: { children: ReactNode }) => {
  const windows = useRef(new Map<string, EventListWindow>());

  const value = useMemo<RememberedWindows>(
    () => ({
      remember: (viewId, window) => {
        windows.current.set(viewId, window);
      },
      get: (viewId) => windows.current.get(viewId),
    }),
    []
  );

  return (
    <RememberedEventListWindowsContext.Provider value={value}>
      {children}
    </RememberedEventListWindowsContext.Provider>
  );
};

/**
 * Windows the author has set, held for as long as they are editing this planner.
 *
 * Owned by CustomizeSidebar, which is mounted on canCustomize rather than on the
 * panel being open — so this survives switching to Style or Schedule and closing
 * customize, both of which unmount the Views panel and would take a ref held
 * there with them. It goes when the sidebar does, on leaving the experience.
 *
 * A ref rather than state: the value is read when a mode is picked, never
 * rendered, so nothing needs to re-render when it changes.
 *
 * Emptied when the planner changes. The sidebar unmounts on the way out of an
 * experience today, which would clear this anyway — but resetting on the id makes
 * that a property of this component rather than one inherited from where it
 * happens to be mounted, and one a test can actually reach.
 */
export const RememberedEventListWindowsProvider = ({
  experienceId,
  children,
}: {
  experienceId: string;
  children: ReactNode;
}) => (
  <RememberedWindowsScope key={experienceId}>{children}</RememberedWindowsScope>
);
